import { Component, EventEmitter, OnDestroy, OnInit, Output } from '@angular/core';
import { CustomerInfoStore } from '../../../stores/customer-info/customer-info.store';
import { CalendarEvent } from '../../../models/minutpass/full-calendar-model';
import { Subscription } from 'rxjs';
import { faCheck, faClock, faComment, faCopy, faList, faPen, faTrash, faUser } from '@fortawesome/free-solid-svg-icons';
import { CustomerService } from '../../../services/customer.service';
import { copyToClipboard, dateBefore } from '../../../services/helpers/utils';
import { MinutPassService } from '../../../services/minut-pass.service';
import { AppointmentStore } from '../../../stores/appointment/appointment.store';
import { Router } from '@angular/router';
import { ToastService } from '../../../services/toast.service';
import { NavigationService } from '../../../services/navigation.service';
import { logError } from 'src/app/services/helpers/sentry.helper';

@Component({
  selector: 'ng-casto-customer-info-appointment',
  templateUrl: './customer-info-appointment.component.html',
})
export class CustomerInfoAppointmentComponent implements OnInit, OnDestroy {
  @Output() onDelete = new EventEmitter<boolean>();
  @Output() onEdit = new EventEmitter<boolean>();

  protected appointment: CalendarEvent;

  private subscription: Subscription;

  protected copyLoading: boolean = false;
  protected copyEnded: boolean = false;

  protected warningDeleteDialogVisible: boolean = false;

  protected deleteAppointmentLoading: boolean = false;

  get displayedAppointmentHours(): string {
    return `De ${this.appointment?.start.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })} à ${this.appointment?.end.toLocaleTimeString('fr-FR', {
      hour: '2-digit',
      minute: '2-digit',
      hour12: false,
    })}`;
  }

  get displayAppointmentDate(): string {
    return this.appointment?.start.toLocaleDateString('fr-FR', {
      weekday: 'long',
      day: 'numeric',
      month: 'long',
      year: 'numeric',
      hour: 'numeric',
      minute: 'numeric',
    });
  }

  get isAppointmentPassed() {
    if (!this.appointment) {
      return true;
    }
    return dateBefore(new Date(this.appointment.start), new Date(), false);
  }

  get customerFullName(): string {
    if (!this.appointment) return '';
    if (this.customerService.userDatas) {
      return `${this.customerService.userDatas.firstname ?? ''} ${this.customerService.userDatas.lastname ?? ''}`;
    } else if (
      this.appointment.extendedProps.customer?.firstname === undefined &&
      this.appointment.extendedProps.customer?.lastname === undefined
    ) {
      return `${this.appointment.extendedProps.customer?.email}`;
    } else {
      return `${this.appointment.extendedProps.customer?.firstname ?? ''} ${this.appointment.extendedProps.customer?.lastname ?? ''}`;
    }
  }

  constructor(
    private customerInfoStore: CustomerInfoStore,
    private appointmentStore: AppointmentStore,
    private minutPassService: MinutPassService,
    private toastService: ToastService,
    private navigationService: NavigationService,
    private customerService: CustomerService,
    private router: Router
  ) {}

  ngOnInit(): void {
    this.subscription = this.customerInfoStore.getAppointment().subscribe(appointment => {
      this.appointment = appointment;
    });
  }

  ngOnDestroy() {
    this.subscription?.unsubscribe();
  }

  protected async copyCustomerName() {
    this.copyLoading = true;
    setTimeout(async () => {
      try {
        await copyToClipboard(this.customerFullName);
        this.copyEnded = true;
      } catch (e) {
        logError(e);
      } finally {
        this.copyLoading = false;
      }
    }, 150);
  }

  protected async deleteAppointment() {
    try {
      this.deleteAppointmentLoading = true;
      await this.minutPassService.deleteAppointment({ id: this.appointment.id });
      this.onDeleteAppointment();
    } catch (e) {
      logError(e);
    } finally {
      this.deleteAppointmentLoading = false;
      this.warningDeleteDialogVisible = false;
    }
  }

  protected async editAppointment() {
    //Je sauvegarde l'url actuelle pour y retourner après la modification du RDV
    this.navigationService.setSavedUrl(this.router.url);
    //Je desactive le fait de vouloir déconnecter le customer à la fermeture
    this.customerInfoStore.setNeedLogoutOnClose(false);
    //Je stocke le rendez-vous dans le store pour qu'il puisse être récupéré dans la page de RDV
    this.appointmentStore.setCurrentAppointment(this.appointment);
    //J'ouvre la page de prise de RDV
    this.router.navigate(['rendez-vous', 'creation']);
    //je ferme la modale
    this.onEdit.emit(true);
  }

  private onDeleteAppointment(): void {
    this.appointmentStore.requestRefresh();
    this.toastService.showSuccessToast('Succès', 'Le rendez-vous a bien été supprimé');
    this.onDelete.emit(true);
  }

  protected readonly faUser = faUser;
  protected readonly faCopy = faCopy;
  protected readonly faClock = faClock;
  protected readonly faList = faList;
  protected readonly faComment = faComment;
  protected readonly faPen = faPen;
  protected readonly faTrash = faTrash;
  protected readonly faCheck = faCheck;
}
